import { Injectable } from '@angular/core';

import { NgRedux } from '@angular-redux/store';

import { CardGroup, RelatedCard, RelatedCardDTO, RidangoReduxAction } from '@ridango/core';

import { Observable } from 'rxjs';

import { RelatedCardUserActions } from '../enums';
import { RelatedCardActions } from './related-card.actions';
import { RelatedCardPartialStoreState, RELATED_CARD_STATE_KEY } from './related-card.reducer';
import * as RelatedCardSelectors from './related-card.selectors';

@Injectable()
export class RelatedCardFacade {
    loaded$ = this.store.select(RelatedCardSelectors.getLoaded);
    error$ = this.store.select(RelatedCardSelectors.getError);
    editMode$ = this.store.select(RelatedCardSelectors.getSelectedRelatedCardEditMode);
    isActionCompleted$ = this.store.select(RelatedCardSelectors.getIsActionCompleted);
    isBlockingFinished$ = this.store.select(RelatedCardSelectors.getBlockingFinished);
    isUnBlockingFinished$ = this.store.select(RelatedCardSelectors.getUnBlockingFinished);

    // all regardless of type
    allRelatedCards$ = this.store.select(RelatedCardSelectors.getAllRelatedCards);
    allDiscountGroups$ = this.store.select(RelatedCardSelectors.getAllDiscountGroups);

    // specific type selectors
    typeEmailCards$ = this.store.select(RelatedCardSelectors.getTypeEmailCards);
    typeTravelcardCards$ = this.store.select(RelatedCardSelectors.getTypeTravelcardCards);
    typePersonalCodeCards$ = this.store.select(RelatedCardSelectors.getTypePresonalCodeCards);

    selectedCard$ = this.store.select(RelatedCardSelectors.getSelectedRelatedCard);
    selectedCardIsBlocked$ = this.store.select(RelatedCardSelectors.getSelectedRelatedCardIsBlocked);
    selectedCardUserActions$ = this.store.select(RelatedCardSelectors.getSelectedRelatedCardUserActions);
    selectedDiscountGroup$ = this.store.select(RelatedCardSelectors.getSelectedDiscountGroup);

    cardStatus$ = this.store.select(RelatedCardSelectors.getCardStatus);

    seutulippuStatus$ = this.store.select(RelatedCardSelectors.getSeutulippuStatus);
    seutulippuLoading$ = this.store.select(RelatedCardSelectors.getSeutulippuLoading);
    seutulippuError$ = this.store.select(RelatedCardSelectors.getSeutulippuError);

    selectedCardsIds$ = this.store.select(RelatedCardSelectors.getSelectedCardsIds);
    sortField$ = this.store.select(RelatedCardSelectors.getSortField);
    sortDirection$ = this.store.select(RelatedCardSelectors.getSortDirection);
    allNormalized$ = this.store.select(RelatedCardSelectors.getAllNormalized);
    allIds$ = this.store.select(RelatedCardSelectors.getAllIds);

    constructor(private store: NgRedux<RelatedCardPartialStoreState>, private actions: RelatedCardActions) {}

    dispatch(action: RidangoReduxAction) {
        this.store.dispatch(action);
    }

    fetchAllRelatedCards() {
        this.dispatch(this.actions.fetchAllRelatedCards());
    }

    fetchCardsDiscountGroups() {
        this.dispatch(this.actions.fetchCardsDiscountGroups());
    }

    addCardsDiscountGroups(discounts: CardGroup[]) {
        this.dispatch(this.actions.addCardsDiscountGroups(discounts));
    }

    setSelectedDiscount(discount: CardGroup) {
        this.dispatch(this.actions.setSelectedDiscount(discount));
    }

    setSelectedRelatedCard(card: RelatedCard) {
        this.dispatch(this.actions.setSelectedRelatedCard(card));
    }

    // this is how dynamic quieries to state should be made ?
    getRelatedCardById(id: number): Observable<RelatedCard> {
        // state[RELATED_CARD_STATE_KEY].allNormalized[state[RELATED_CARD_STATE_KEY].selectedId]
        return this.store.select([RELATED_CARD_STATE_KEY, 'allNormalized', id]);
    }

    setSelectedCardActions(actions: RelatedCardUserActions[]) {
        this.dispatch(this.actions.setSelectedCardUserActions(actions));
    }

    resetRelatedCards() {
        this.dispatch(this.actions.resetRelatedCards());
    }

    resetRelatedCardStatus() {
        this.dispatch(this.actions.resetRelatedCardStatus());
    }

    setEditMode(status: boolean) {
        this.dispatch(this.actions.setEditMode(status));
    }

    saveNewRelatedCard(cardDTO: RelatedCardDTO) {
        this.dispatch(this.actions.saveNewRelatedCard(cardDTO));
    }

    saveEditedRelatedCard(cardId: number, payload: any) {
        this.dispatch(this.actions.saveEditedRelatedCard({ cardId, payload }));
    }

    delateRelatedCard(cardId: number) {
        this.dispatch(this.actions.deleteRelatedCard(cardId));
    }

    blockRelatedCard(card: RelatedCard) {
        this.dispatch(this.actions.blockRelatedCard(card));
    }

    blockAndReplaceRelatedCard(card: RelatedCard, payload: any) {
        this.dispatch(this.actions.blockAndReplaceRelatedCard(card, payload));
    }

    unBlockRelatedCard(card: RelatedCard) {
        this.dispatch(this.actions.unBlockRelatedCard(card));
    }

    fetchCardStatus(cardNumber: number, cardType: string) {
        this.dispatch(this.actions.fetchCardStatus(cardNumber, cardType));
    }

    saveSeutulippu(payload) {
        this.dispatch(this.actions.saveSeutulippuCard(payload));
    }

    toggleSelectedRelatedCard(cardId) {
        this.dispatch(this.actions.toggleSelectedRelatedCard(cardId));
    }

    toggleAllRelatedCards() {
        this.dispatch(this.actions.toggleAllRelatedCards());
    }

    sortByNumber() {
        this.dispatch(this.actions.sortRelatedCards('card_nr'));
    }

    sortByName() {
        this.dispatch(this.actions.sortRelatedCards('name'));
    }
}
